import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Rocket, Calendar, MapPin, Plus, X, ChevronRight, ChevronLeft, Eye, Check, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { projectsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';

const GENRES = ['music', 'film', 'art', 'tech', 'fashion', 'gaming', 'education', 'community', 'other'];
const STEPS = ['Details', 'Tiers', 'Event', 'Preview'];
const SIMPLE_STEPS = ['Details', 'Tiers', 'Preview'];

const DEFAULT_FORM = {
  title: '',
  description: '',
  genre: 'music',
  funding_goal: '',
  deadline_days: '30',
  tiers: [],
  create_event: false,
  event_location: '',
  event_venue: '',
  event_date: '',
  event_time: '20:00',
  ticket_tiers: [],
};

const decodePrefill = (value) => {
  if (!value) return null;
  try { return JSON.parse(decodeURIComponent(value)); } catch { return null; }
};

const normalizePrefill = (prefill) => {
  if (!prefill) return null;
  return {
    title: prefill.title || '',
    description: prefill.description || '',
    genre: prefill.genre || 'music',
    funding_goal: prefill.funding_goal || '',
    deadline_days: prefill.deadline_days || '30',
    tiers: Array.isArray(prefill.tiers) ? prefill.tiers : [],
    create_event: Boolean(prefill.create_event),
    event_location: prefill.event_location || '',
    event_venue: prefill.event_venue || '',
    event_date: prefill.event_date || '',
    event_time: prefill.event_time || '20:00',
    ticket_tiers: Array.isArray(prefill.ticket_tiers) ? prefill.ticket_tiers : [],
  };
};

export default function CreateProjectPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('simple');
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [newTier, setNewTier] = useState({ name: '', amount: '', perks: '', limit: '' });
  const [newTicketTier, setNewTicketTier] = useState({ name: '', price: '', quantity: '' });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const steps = mode === 'bundle' ? STEPS : SIMPLE_STEPS;

  useEffect(() => {
    const prefill = normalizePrefill(location.state?.prefill || decodePrefill(searchParams.get('prefill')));
    if (!prefill) return;
    setForm(prev => ({ ...prev, ...prefill }));
    setMode(prefill.create_event ? 'bundle' : 'simple');
    setPrefillApplied(true);
  }, [location.state, searchParams]);

  const addTier = () => {
    if (!newTier.name || !newTier.amount) return;
    setForm(prev => ({ ...prev, tiers: [...prev.tiers, { ...newTier, amount: parseFloat(newTier.amount), limit: parseInt(newTier.limit) || 0 }] }));
    setNewTier({ name: '', amount: '', perks: '', limit: '' });
  };
  const removeTier = (idx) => setForm(prev => ({ ...prev, tiers: prev.tiers.filter((_, i) => i !== idx) }));

  const addTicketTier = () => {
    if (!newTicketTier.name || !newTicketTier.price) return;
    setForm(prev => ({ ...prev, ticket_tiers: [...prev.ticket_tiers, { name: newTicketTier.name, price: parseFloat(newTicketTier.price), quantity: parseInt(newTicketTier.quantity, 10) || 100 }] }));
    setNewTicketTier({ name: '', price: '', quantity: '' });
  };
  const removeTicketTier = (idx) => setForm(prev => ({ ...prev, ticket_tiers: prev.ticket_tiers.filter((_, i) => i !== idx) }));

  const canAdvance = () => {
    if (steps[step] === 'Details') return form.title && form.description && form.funding_goal;
    return true;
  };

  const nextStep = () => { if (canAdvance() && step < steps.length - 1) setStep(s => s + 1); };
  const prevStep = () => { if (step > 0) setStep(s => s - 1); };

  const handleSubmit = async () => {
    if (!form.title || !form.description || !form.funding_goal) { toast.error('Fill in title, description, and funding goal'); return; }
    setSubmitting(true);
    try {
      if (mode === 'bundle') {
        const res = await projectsAPI.launchBundle({
          title: form.title, description: form.description, genre: form.genre,
          funding_goal: parseFloat(form.funding_goal), deadline_days: parseInt(form.deadline_days),
          tiers: form.tiers, create_event: form.create_event,
          event_location: form.event_location, event_venue: form.event_venue,
          event_date: form.event_date, event_time: form.event_time, ticket_tiers: form.ticket_tiers,
        });
        toast.success('Launched project' + (res.data.event ? ' + event!' : '!'));
        navigate(`/projects/${res.data.project.id}`);
      } else {
        const res = await projectsAPI.create({
          title: form.title, description: form.description, genre: form.genre,
          funding_goal: parseFloat(form.funding_goal), deadline_days: parseInt(form.deadline_days), tiers: form.tiers,
        });
        toast.success('Project launched!');
        navigate(`/projects/${res.data.id}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create project'); }
    finally { setSubmitting(false); }
  };

  if (!user) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><p className="text-zinc-500">Please log in</p></div>;

  return (
    <div className="min-h-screen bg-zinc-950 pb-20" data-testid="create-project-page">
      <div className="max-w-2xl mx-auto px-4 pt-8">
        <Link to="/projects" className="text-zinc-500 hover:text-white mb-6 inline-flex items-center gap-1 text-sm" data-testid="create-project-back-link"><ArrowLeft size={16} /> Back to Projects</Link>

        <h1 className="font-display font-bold text-3xl text-white mb-2">Launch Your Project</h1>
        <p className="text-zinc-500 text-sm mb-6">Create a funding campaign. Fans can back you from $1.</p>

        {prefillApplied && (
          <div className="mb-6 rounded-2xl border border-[#b8ff00]/20 bg-[#b8ff00]/5 p-4" data-testid="funnel-prefill-banner">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b8ff00]"><Sparkles size={12} className="inline mr-1" />AI funnel draft loaded</p>
            <p className="mt-1 text-sm text-zinc-300">Your project and event bundle were prefilled from the Funding Funnel.</p>
          </div>
        )}

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => { setMode('simple'); update('create_event', false); setStep(0); }} className={`flex-1 p-4 rounded-xl border-2 text-left transition-all ${mode === 'simple' ? 'border-[#b8ff00] bg-[#b8ff00]/5' : 'border-zinc-800 hover:border-zinc-700'}`} data-testid="mode-simple">
            <p className="font-bold text-white text-sm">Simple Launch</p>
            <p className="text-xs text-zinc-500">Just the funding project</p>
          </button>
          <button onClick={() => { setMode('bundle'); update('create_event', true); setStep(0); }} className={`flex-1 p-4 rounded-xl border-2 text-left transition-all ${mode === 'bundle' ? 'border-[#b8ff00] bg-[#b8ff00]/5' : 'border-zinc-800 hover:border-zinc-700'}`} data-testid="mode-bundle">
            <div className="flex items-center gap-2 mb-1">
              <Rocket size={14} className="text-[#b8ff00]" />
              <p className="font-bold text-white text-sm">Launch Bundle</p>
            </div>
            <p className="text-xs text-zinc-500">Project + Live Event together</p>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8" data-testid="wizard-steps">
          {steps.map((s, idx) => (
            <React.Fragment key={s}>
              <button
                onClick={() => idx <= step && setStep(idx)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  idx === step ? 'bg-[#b8ff00] text-black' :
                  idx < step ? 'bg-zinc-800 text-[#b8ff00]' :
                  'bg-zinc-900 text-zinc-600'
                }`}
                data-testid={`wizard-step-${s.toLowerCase()}`}
              >
                {idx < step ? <Check size={12} /> : <span className="w-4 text-center">{idx + 1}</span>}
                {s}
              </button>
              {idx < steps.length - 1 && <div className={`flex-1 h-px ${idx < step ? 'bg-[#b8ff00]/40' : 'bg-zinc-800'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>

            {/* Step: Details */}
            {steps[step] === 'Details' && (
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6 space-y-4">
                <h2 className="font-bold text-white">Project Details</h2>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Title</label>
                  <Input value={form.title} onChange={e => update('title', e.target.value)} placeholder="My Amazing EP" className="bg-zinc-800 border-zinc-700 text-white" data-testid="project-title-input" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Description</label>
                  <Textarea value={form.description} onChange={e => update('description', e.target.value)} placeholder="Tell backers what you're creating and why it matters..." rows={4} className="bg-zinc-800 border-zinc-700 text-white" data-testid="project-desc-input" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Genre</label>
                    <select value={form.genre} onChange={e => update('genre', e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2 text-sm" data-testid="genre-select">
                      {GENRES.map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Funding Goal ($)</label>
                    <Input type="number" min={100} value={form.funding_goal} onChange={e => update('funding_goal', e.target.value)} placeholder="5000" className="bg-zinc-800 border-zinc-700 text-white" data-testid="funding-goal-input" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Days</label>
                    <Input type="number" min={7} max={90} value={form.deadline_days} onChange={e => update('deadline_days', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="deadline-input" />
                  </div>
                </div>
              </div>
            )}

            {/* Step: Tiers */}
            {steps[step] === 'Tiers' && (
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6 space-y-4">
                <h2 className="font-bold text-white">Investment Tiers <span className="text-xs text-zinc-500 font-normal">(optional — defaults provided)</span></h2>
                {form.tiers.map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-zinc-800 rounded-xl">
                    <div>
                      <span className="font-semibold text-white text-sm">{t.name}</span>
                      <span className="ml-2 text-[#b8ff00] text-sm font-bold">${t.amount}</span>
                      {t.perks && <p className="text-xs text-zinc-500">{t.perks}</p>}
                    </div>
                    <button onClick={() => removeTier(i)} className="text-zinc-500 hover:text-red-400" data-testid={`remove-tier-${i}`}><X size={14} /></button>
                  </div>
                ))}
                <div className="grid grid-cols-4 gap-2">
                  <Input placeholder="Name" value={newTier.name} onChange={e => setNewTier({ ...newTier, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-tier-name-input" />
                  <Input type="number" placeholder="$" value={newTier.amount} onChange={e => setNewTier({ ...newTier, amount: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-tier-amount-input" />
                  <Input placeholder="Perks" value={newTier.perks} onChange={e => setNewTier({ ...newTier, perks: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-tier-perks-input" />
                  <Button onClick={addTier} className="bg-zinc-700 text-white hover:bg-zinc-600 text-sm" data-testid="add-tier-button"><Plus size={14} className="mr-1" /> Add</Button>
                </div>
                {form.tiers.length === 0 && <p className="text-xs text-zinc-600 text-center py-2">No custom tiers yet. Default $1, $10, $50, $100 tiers will apply.</p>}
              </div>
            )}

            {/* Step: Event (Bundle only) */}
            {steps[step] === 'Event' && (
              <div className="bg-zinc-900 rounded-2xl border border-[#b8ff00]/20 p-6 space-y-4">
                <h2 className="font-bold text-white flex items-center gap-2"><Calendar size={16} className="text-[#b8ff00]" /> Live Event</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Location</label>
                    <Input value={form.event_location} onChange={e => update('event_location', e.target.value)} placeholder="Los Angeles, CA" className="bg-zinc-800 border-zinc-700 text-white" data-testid="event-location-input" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Venue</label>
                    <Input value={form.event_venue} onChange={e => update('event_venue', e.target.value)} placeholder="The Wiltern" className="bg-zinc-800 border-zinc-700 text-white" data-testid="event-venue-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Date</label>
                    <Input type="date" value={form.event_date} onChange={e => update('event_date', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="event-date-input" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Time</label>
                    <Input type="time" value={form.event_time} onChange={e => update('event_time', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="event-time-input" />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-xs text-zinc-500 block">Ticket tiers</label>
                  {form.ticket_tiers.map((tier, index) => (
                    <div key={`${tier.name}-${index}`} className="flex items-center justify-between rounded-xl bg-zinc-800 p-3">
                      <div>
                        <p className="font-semibold text-white text-sm">{tier.name}</p>
                        <p className="text-xs text-zinc-500">${tier.price} / {tier.quantity} tickets</p>
                      </div>
                      <button onClick={() => removeTicketTier(index)} className="text-zinc-500 hover:text-red-400" data-testid={`remove-ticket-tier-${index}`}><X size={14} /></button>
                    </div>
                  ))}
                  <div className="grid grid-cols-4 gap-2">
                    <Input placeholder="Tier" value={newTicketTier.name} onChange={e => setNewTicketTier({ ...newTicketTier, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="ticket-tier-name-input" />
                    <Input type="number" placeholder="Price" value={newTicketTier.price} onChange={e => setNewTicketTier({ ...newTicketTier, price: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="ticket-tier-price-input" />
                    <Input type="number" placeholder="Qty" value={newTicketTier.quantity} onChange={e => setNewTicketTier({ ...newTicketTier, quantity: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="ticket-tier-quantity-input" />
                    <Button onClick={addTicketTier} className="bg-zinc-700 text-white hover:bg-zinc-600 text-sm" data-testid="add-ticket-tier-button"><Plus size={14} className="mr-1" /> Add</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step: Preview */}
            {steps[step] === 'Preview' && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6" data-testid="launch-preview">
                  <div className="flex items-center gap-2 mb-4">
                    <Eye size={16} className="text-[#b8ff00]" />
                    <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#b8ff00]">Investor Preview</span>
                  </div>
                  <h2 className="font-display font-bold text-2xl text-white mb-2">{form.title || 'Untitled Project'}</h2>
                  <p className="text-sm text-zinc-400 mb-4">{form.description || 'No description'}</p>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-zinc-800 rounded-xl p-3 text-center">
                      <p className="text-xs text-zinc-500">Goal</p>
                      <p className="font-display font-bold text-lg text-[#b8ff00]">{formatCurrency(parseFloat(form.funding_goal) || 0)}</p>
                    </div>
                    <div className="bg-zinc-800 rounded-xl p-3 text-center">
                      <p className="text-xs text-zinc-500">Genre</p>
                      <p className="font-semibold text-white text-sm">{form.genre}</p>
                    </div>
                    <div className="bg-zinc-800 rounded-xl p-3 text-center">
                      <p className="text-xs text-zinc-500">Duration</p>
                      <p className="font-semibold text-white text-sm">{form.deadline_days} days</p>
                    </div>
                  </div>
                  {form.tiers.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-zinc-500 mb-2">Investment Tiers</p>
                      <div className="flex flex-wrap gap-2">
                        {form.tiers.map((t, i) => (
                          <span key={i} className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-zinc-300">
                            {t.name} — <span className="text-[#b8ff00] font-bold">${t.amount}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {mode === 'bundle' && (
                    <div className="border-t border-zinc-800 pt-4 mt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar size={14} className="text-amber-400" />
                        <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Bundled Event</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-zinc-500">Location:</span> <span className="text-white">{form.event_location || '—'}</span></div>
                        <div><span className="text-zinc-500">Venue:</span> <span className="text-white">{form.event_venue || '—'}</span></div>
                        <div><span className="text-zinc-500">Date:</span> <span className="text-white">{form.event_date || '—'}</span></div>
                        <div><span className="text-zinc-500">Time:</span> <span className="text-white">{form.event_time || '—'}</span></div>
                      </div>
                      {form.ticket_tiers.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {form.ticket_tiers.map((t, i) => (
                            <span key={i} className="px-3 py-1.5 bg-zinc-800 border border-amber-500/20 rounded-lg text-xs text-zinc-300">
                              {t.name} — ${t.price} ({t.quantity} tickets)
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-center text-xs text-zinc-600">This is how investors will see your project. Ready?</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 gap-4">
          <Button
            onClick={prevStep}
            disabled={step === 0}
            variant="outline"
            className="border-zinc-700 text-zinc-400 hover:text-white"
            data-testid="wizard-prev-btn"
          >
            <ChevronLeft size={16} className="mr-1" /> Back
          </Button>

          {steps[step] === 'Preview' ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#b8ff00] text-black font-bold text-base h-12 px-8 rounded-xl hover:bg-[#a0e600] flex-1"
              data-testid="launch-project-btn"
            >
              <Rocket className="mr-2" size={18} />
              {submitting ? 'Launching...' : mode === 'bundle' ? 'Launch Project + Event' : 'Launch Project'}
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              disabled={!canAdvance()}
              className="bg-zinc-800 text-white font-semibold px-8 h-12 rounded-xl hover:bg-zinc-700"
              data-testid="wizard-next-btn"
            >
              Next: {steps[step + 1]} <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
